import { ComponentPublicInstance, defineComponent } from "vue";

import axios from "axios";
import { get, isNil, set } from "lodash";

import { ContextKeys } from "@/enums/contextKeys.enum";
import { Events } from "@/enums/events.enum";
import { InterruptPipelineActionError } from "@/core/errors/actions/BaseActionError";
import {
  FileUploadValue,
  exceedsMaxFileSize,
  isBelowMinWidth,
  isOlderThanAllowed,
} from "@/helpers/fileUploadValidators";

interface FileUploadVm extends ComponentPublicInstance {
  path: string;
  root: string;
  ref: string;
  settings: any;
  resolvedClientSideEvents: any;
  processEvent: (params: { events: any; eventName: string; context?: any }) => Promise<any>;
  error: string | null;
  file: File | null;
  loading: boolean;
  previewObjectUrl: string | null;
  revokePreviewObjectUrl: () => void;
}

export default defineComponent({
  data() {
    return {
      error: null as string | null,
      file: null as File | null,
      loading: false as boolean,
      previewObjectUrl: null as string | null,
    };
  },

  computed: {
    value: {
      get(): FileUploadValue | null {
        const self = this as unknown as FileUploadVm;

        return get(self, self.path, null) as FileUploadValue | null;
      },
      set(newValue: FileUploadValue | null) {
        const self = this as unknown as FileUploadVm;

        set(self, self.path, newValue);
      },
    },

    contentType: {
      get(): string | null {
        return (this as any).value?.content_type ?? null;
      },
      set(newValue: string | null) {
        const self = this as unknown as FileUploadVm;

        set(self, `${self.path}.content_type`, newValue);
      },
    },

    documentId: {
      get(): string | null {
        return (this as any).value?.document_id ?? null;
      },
      set(newValue: string | null) {
        const self = this as unknown as FileUploadVm;

        set(self, `${self.path}.document_id`, newValue);
      },
    },

    comments: {
      get(): string | null {
        return (this as any).value?.comments ?? null;
      },
      set(newValue: string | null) {
        const self = this as unknown as FileUploadVm;

        set(self, `${self.path}.comments`, newValue);
      },
    },

    acceptAttr(): string | undefined {
      const allowedMimeTypes: string[] | null = get((this as any).settings, "restrictions.allowedMimeTypes", null);

      return allowedMimeTypes?.length ? allowedMimeTypes.join(",") : undefined;
    },

    hasFile(): boolean {
      return !isNil((this as any).contentType) || !isNil((this as any).documentId);
    },

    canPreview(): boolean {
      return !isNil((this as any).file) || !isNil((this as any).documentId);
    },

    icon(): string {
      return (this as any).hasFile ? "fa-xmark" : "file-arrow-up";
    },
  },

  unmounted() {
    (this as any).revokePreviewObjectUrl();
  },

  methods: {
    async executePreviewAction({ action, context = {} }: { action: any; context?: any }) {
      const tabTarget = get(action, "settings.tabTarget", "_blank");
      const presignedUrl = get(context, get(action, "settings.source"));

      window.open(presignedUrl, tabTarget);
    },

    async executePutObjectAction({ action, context = {} }: { action: any; context?: any }) {
      const self = this as unknown as FileUploadVm;

      try {
        await axios.put(get(context, get(action, "settings.url")), get(context, get(action, "settings.object")), {
          headers: {
            "Content-Type": get(context, get(action, "settings.contentType")),
          },
        });
      } catch (error: any) {
        self.error = get(error, "message", "Error al cargar archivo") as string;
        self.loading = false;

        throw new InterruptPipelineActionError(action, self.error, context, error);
      }
    },

    async preview() {
      const self = this as unknown as FileUploadVm;

      if (self.file) {
        self.revokePreviewObjectUrl();
        self.previewObjectUrl = URL.createObjectURL(self.file);
        window.open(self.previewObjectUrl, "_blank");
      } else {
        await self.processEvent({
          events: self.resolvedClientSideEvents,
          eventName: `$this.${Events.PREVIEW}`,
          context: {
            [ContextKeys.THIS]: self,
            [ContextKeys.FORM]: get(self, self.root, {}),
          },
        });
      }
    },

    revokePreviewObjectUrl() {
      const self = this as unknown as FileUploadVm;

      if (self.previewObjectUrl) {
        URL.revokeObjectURL(self.previewObjectUrl);
        self.previewObjectUrl = null;
      }
    },

    resetInput() {
      const self = this as unknown as FileUploadVm;

      if (self.$refs[self.ref]) {
        (self.$refs[self.ref] as any).value = null;
      }
    },

    async validateFile(file: File): Promise<string | null> {
      const restrictions = get((this as any).settings, "restrictions", {});

      const maxFileSize: number | null = get(restrictions, "maxFileSize", null);
      if (maxFileSize && exceedsMaxFileSize(file, maxFileSize)) {
        return "El archivo excede el máximo permitido.";
      }

      const lastModifiedSettings = get(restrictions, "validateLastModified", null);
      if (lastModifiedSettings && isOlderThanAllowed(file, lastModifiedSettings)) {
        return `La fecha de creación debe ser menor a un ${Math.abs(get(lastModifiedSettings, "delta.day"))} día(s)`;
      }

      const dimensions = get(restrictions, "dimensions", null);
      if (dimensions) {
        const minWidth: number = get(dimensions, "minWidth", 1280);

        if (await isBelowMinWidth(file, minWidth)) {
          return `La imagen debe tener al menos ${minWidth}px de ancho`;
        }
      }

      return null;
    },
  },
});
